/*
  HandwritingMappingDialog — Stage 2 of the Handwriting Recognition spec.

  After the practitioner has confirmed extracted blocks, this dialog
  shows a checklist of proposed writes into the structured PHR:
    - Medications
    - Allergies
    - Medical history
    - Personal / contact fields
    - Emergency contacts

  Every row shows the parsed payload, an action selector
  (Create / Update / Skip), and — when a duplicate exists on the
  client — the summary of the existing record so the reviewer can
  choose to skip, overwrite, or add anyway.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Loader2, CheckCircle2, AlertTriangle, ShieldAlert, ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

const SECTION_LABEL = {
  medications: "Medications",
  allergies: "Allergies",
  medical_history: "Medical history",
  personal: "Personal & contact",
  emergency_contact: "Emergency contact",
};

const ACTION_TONE = {
  create: "bg-emerald-50 text-emerald-800 border-emerald-300",
  update: "bg-sky-50 text-sky-800 border-sky-300",
  skip:   "bg-zinc-100 text-zinc-600 border-zinc-300",
};


export default function HandwritingMappingDialog({ uploadId, open, onOpenChange, onApplied }) {
  const [proposals, setProposals] = useState(null);
  const [decisions, setDecisions] = useState({});   // action_id -> action
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    try {
      const r = await api.get(`/handwriting/uploads/${uploadId}/mapping/preview`);
      setProposals(r.data.proposals || []);
      // Default: mirror the server's recommendation (create/skip).
      const init = {};
      for (const p of r.data.proposals || []) init[p.action_id] = p.action;
      setDecisions(init);
    } catch (e) {
      toast.error("Couldn't build mapping preview", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, [uploadId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const grouped = useMemo(() => {
    const bySec = {};
    for (const p of proposals || []) (bySec[p.section] ||= []).push(p);
    return bySec;
  }, [proposals]);

  const summary = useMemo(() => {
    let create = 0, update = 0, skip = 0;
    for (const p of proposals || []) {
      const a = decisions[p.action_id] || "skip";
      if (a === "create") create++;
      else if (a === "update") update++;
      else skip++;
    }
    return { create, update, skip };
  }, [proposals, decisions]);

  async function apply() {
    setApplying(true);
    try {
      const payload = {
        decisions: (proposals || []).map((p) => ({
          ...p, action: decisions[p.action_id] || "skip",
        })),
      };
      const r = await api.post(
        `/handwriting/uploads/${uploadId}/mapping/apply`,
        payload,
      );
      const c = r.data.counts;
      toast.success(
        `Applied — ${c.created} created, ${c.updated} updated, ${c.skipped} skipped`,
      );
      onApplied?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Apply failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setApplying(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[92vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="hw-mapping-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-700" />
            Auto-populate the client record
          </DialogTitle>
          <DialogDescription>
            Only confirmed blocks appear here. Rows matching an existing record on
            file default to <span className="font-medium">Skip</span> — flip to
            Update if you want to overwrite, or Create anyway to keep both.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          {loading ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Building preview…
            </div>
          ) : !proposals || proposals.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              Nothing to map yet — confirm one or more extracted blocks first.
            </div>
          ) : (
            Object.entries(grouped).map(([section, list]) => (
              <div key={section} className="mb-5">
                <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1">
                  {SECTION_LABEL[section] || section}
                </div>
                <div className="space-y-2">
                  {list.map((p) => {
                    const action = decisions[p.action_id] || p.action;
                    const hasDup = !!p.existing_id;
                    return (
                      <div
                        key={p.action_id}
                        className={cn(
                          "border rounded p-2.5 bg-white",
                          p.is_sensitive ? "border-red-200 bg-red-50/30" : "border-zinc-200",
                        )}
                        data-testid={`hw-mapping-row-${p.action_id}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-zinc-900 truncate flex items-center gap-2">
                              <span className="font-medium">{p.source_label || "Field"}</span>
                              {p.is_sensitive && (
                                <Badge className="text-2xs uppercase border bg-red-50 text-red-700 border-red-200 flex items-center gap-1">
                                  <ShieldAlert className="h-3 w-3" /> Sensitive
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-zinc-600 mt-1 font-mono-num">
                              {formatPayload(section, p.payload)}
                            </div>
                            {hasDup && (
                              <div className="text-xs text-amber-800 mt-1 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Existing on file: {p.existing_summary}
                              </div>
                            )}
                          </div>
                          <div className="w-32 shrink-0">
                            <Select
                              value={action}
                              onValueChange={(v) =>
                                setDecisions((d) => ({ ...d, [p.action_id]: v }))
                              }
                            >
                              <SelectTrigger className={cn("h-8 text-xs border", ACTION_TONE[action])}
                                             data-testid={`hw-mapping-action-${p.action_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="create">Create new</SelectItem>
                                <SelectItem value="update" disabled={!hasDup}>Update existing</SelectItem>
                                <SelectItem value="skip">Skip</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="border-t pt-3 flex-wrap gap-2 sm:justify-between">
          <div className="text-xs text-zinc-500 flex items-center gap-3">
            <span><Badge className="bg-emerald-50 text-emerald-800 border-emerald-300 mr-1">{summary.create}</Badge>create</span>
            <span><Badge className="bg-sky-50 text-sky-800 border-sky-300 mr-1">{summary.update}</Badge>update</span>
            <span><Badge className="bg-zinc-100 text-zinc-600 border-zinc-300 mr-1">{summary.skip}</Badge>skip</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={apply}
              disabled={applying || (summary.create + summary.update) === 0}
              data-testid="hw-mapping-apply"
            >
              {applying
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Applying…</>
                : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Apply {summary.create + summary.update} change(s)</>
              }
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function formatPayload(section, payload) {
  if (!payload) return "";
  if (section === "medications") {
    return [payload.name, payload.dosage, payload.frequency].filter(Boolean).join(" · ");
  }
  if (section === "allergies") {
    return `${payload.substance} · ${payload.severity}`;
  }
  if (section === "medical_history") {
    return `${(payload.category || "condition")} · ${payload.title}`;
  }
  if (section === "emergency_contact") {
    return [payload.name, payload.relationship, payload.phone].filter(Boolean).join(" · ");
  }
  // Personal: key-value pairs.
  return Object.entries(payload)
    .filter(([k]) => k !== "notes_appended")
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
    .join(" · ");
}
